import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { Observable, map, catchError, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { CotacaoDTO, CotacaoResponseDTO } from '../../models/gerenciar-contas/cotacao.model';

/** Path do endpoint de ultima cotacao - cacheado via shareReplay, tanto
 *  para variante default (sem query) quanto para variantes com moedaPadrao=... */
const URL_ULTIMA_COTACAO = '/cotacao/ultimaCotacao';

@Injectable({
  providedIn: 'root',
})
export class CotacaoService {
  /** Cache por URL normalizada (path + query ordenada) do GET /cotacao/ultimaCotacao.
   *  Evita chamadas duplicadas quando a mesma URL e consultada por diferentes
   *  paginas na mesma carga (ex.: ?moedaPadrao=GUARANI em Transferencias, Nova
   *  Despesa e outras).
   *
   *  Cada entrada tem TTL ({@link CACHE_TTL_MS}): apos expirar, a proxima
   *  consulta refaz o HTTP. Isso defende contra cotacao stale quando OUTRO
   *  usuario/dispositivo cadastra uma cotacao durante a sessao do operador.
   *  Tambem e invalidado explicitamente em logout e ao cadastrar cotacao
   *  via clearCotacaoCache(). */
  private ultimaCotacoesCache: Map<string, { observable: Observable<CotacaoResponseDTO>; expiraEm: number }> = new Map();

  /** Tempo de vida de cada entrada do cache da ultima cotacao (5 minutos). */
  private readonly CACHE_TTL_MS = 5 * 60 * 1000;

  constructor(private apiService: ApiService) {}

  // Método genérico para listagem paginada
  get(url: string, data?: any): Observable<CotacaoResponseDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;

    // Reaproveita o cache da ultima cotacao (com ou sem query params) para
    // evitar chamadas duplicadas entre paginas e o verificador global do app.component.
    // Usamos startsWith para cobrir tanto o path exato quanto variantes como
    // "/cotacao/ultimaCotacao?moedaPadrao=GUARANI".
    if (path === URL_ULTIMA_COTACAO || path.startsWith(URL_ULTIMA_COTACAO + '?')) {
      return this.getUltimaCotacaoCached(path);
    }

    return this.apiService.read<CotacaoResponseDTO>(path);
  }

  /** Retorna o observable cacheado da ultima cotacao para a URL informada.
   *  Refaz o HTTP quando nao ha entrada ou quando a entrada expirou (TTL). */
  private getUltimaCotacaoCached(path: string): Observable<CotacaoResponseDTO> {
    const chave = this.normalizarChaveCache(path);
    const agora = Date.now();
    const entrada = this.ultimaCotacoesCache.get(chave);
    if (entrada && entrada.expiraEm > agora) {
      return entrada.observable;
    }
    const observable = this.apiService
      .read<CotacaoResponseDTO>(path)
      .pipe(shareReplay(1));
    this.ultimaCotacoesCache.set(chave, { observable, expiraEm: agora + this.CACHE_TTL_MS });
    return observable;
  }

  /** Normaliza a URL para a chave de cache: ordena os params da query string
   *  para evitar cache miss por ordem diferente. */
  private normalizarChaveCache(path: string): string {
    const idx = path.indexOf('?');
    if (idx < 0) return path;
    const base = path.substring(0, idx);
    const query = path.substring(idx + 1);
    if (!query) return base;
    const params = query.split('&')
      .filter(p => p.length > 0)
      .sort();
    return params.length > 0 ? `${base}?${params.join('&')}` : base;
  }

  /** Invalida o cache da ultima cotacao. Chamado no logout (evita cache entre
   *  usuarios) e ao cadastrar uma nova cotacao (garante que o PDV/dialogos
   *  passem a usar a cotacao recem-criada na proxima consulta). */
  clearCotacaoCache(): void {
    this.ultimaCotacoesCache.clear();
  }

  // Método para pegar uma única cotação atual
  getCotacaoAtual(moeda: string): Observable<CotacaoDTO> {
    return this.apiService.read<CotacaoDTO>(`/cotacao/cotacaoAtual?moedaPadrao=${moeda}`);
  }

  getById(url: string): Observable<CotacaoDTO> {
    return this.apiService.read<CotacaoDTO>(url);
  }

  post(url: string, data: any): Observable<CotacaoDTO> {
    return this.apiService.create<CotacaoDTO>(url, data);
  }

  put(url: string, data: Partial<CotacaoDTO>): Observable<CotacaoDTO> {
    return this.apiService.update<CotacaoDTO>(url, data);
  }

  delete(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  /**
   * Verifica se existe cotacao registrada para o dia atual.
   * Retorna true se precisa exibir o dialogo (nao tem cotacao do dia).
   * Entre 00h e 06h aceita cotacao do dia anterior.
   */
  verificarPrecisaCotacao(): Observable<boolean> {
    return this.get('/cotacao/ultimaCotacao').pipe(
      map((res: any) => {
        if (res && res.emissao) {
          const dataEmissaoStr = String(res.emissao).substring(0, 10);
          const hoje = new Date();
          const pad = (n: number) => String(n).padStart(2, '0');
          const hojeStr = `${hoje.getFullYear()}-${pad(hoje.getMonth() + 1)}-${pad(hoje.getDate())}`;

          if (dataEmissaoStr === hojeStr) {
            return false; // Cotacao do dia existe, nao precisa
          }

          // Entre meia-noite e 6h: aceita cotacao do dia anterior
          const hora = hoje.getHours();
          if (hora >= 0 && hora < 6) {
            return false; // Aceita cotacao anterior nesse horario
          }
        }

        // Nao tem cotacao do dia e esta apos 6h
        return true;
      }),
      catchError(() => {
        // Erro ao buscar: provavelmente nao tem cotacao, precisa cadastrar
        const hora = new Date().getHours();
        if (hora >= 0 && hora < 6) {
          return of(false); // Madrugada: nao exige
        }
        return of(true);
      })
    );
  }
}
